/**
 * Helpers for narrowing an Excel citation locator to a specific A1 cell range.
 *
 * Case Partner can cite a tighter sub-range than the whole chunk using the
 * `[cite:GUID:chunkId@B12:D20]` syntax. The chunk still resolves the sheet +
 * base locator; the cell range simply overrides the highlighted bounds here so
 * the rest of the viewer pipeline stays untouched.
 */

interface CellRangeBounds {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
  a1: string;
}

const A1_CELL_PATTERN = /^([A-Za-z]+)(\d+)$/;

function columnLettersToIndex(letters: string): number {
  let index = 0;
  for (const char of letters.toUpperCase()) {
    index = index * 26 + (char.charCodeAt(0) - 64);
  }
  return index;
}

export function parseCellRange(cellRange?: string | null): CellRangeBounds | null {
  if (!cellRange) return null;

  const [startRaw, endRaw] = cellRange.trim().split(':');
  const start = startRaw?.match(A1_CELL_PATTERN);
  if (!start) return null;
  const end = endRaw ? endRaw.match(A1_CELL_PATTERN) : start;
  if (!end) return null;

  const startColumn = columnLettersToIndex(start[1]);
  const endColumn = columnLettersToIndex(end[1]);
  const startRow = Number.parseInt(start[2], 10);
  const endRow = Number.parseInt(end[2], 10);

  const a1Start = `${start[1].toUpperCase()}${start[2]}`;
  const a1End = `${end[1].toUpperCase()}${end[2]}`;

  return {
    startRow: Math.min(startRow, endRow),
    endRow: Math.max(startRow, endRow),
    startColumn: Math.min(startColumn, endColumn),
    endColumn: Math.max(startColumn, endColumn),
    a1: endRaw ? `${a1Start}:${a1End}` : a1Start,
  };
}

/**
 * Returns a copy of the locator with its bounds overridden by `cellRange`.
 * If the range is missing or unparseable, the original locator is returned.
 */
export function applyCellRangeToLocator<T extends Record<string, any>>(
  locator: T,
  cellRange?: string | null
): T {
  const bounds = parseCellRange(cellRange);
  if (!bounds) return locator;

  return {
    ...locator,
    range: locator.sheet_name ? `${locator.sheet_name}!${bounds.a1}` : bounds.a1,
    start_row: bounds.startRow,
    end_row: bounds.endRow,
    start_column: bounds.startColumn,
    end_column: bounds.endColumn,
  };
}
